import { useCallback, useEffect, useMemo, useState } from "react";
import { LuUpload } from "react-icons/lu";
import { FaPlus, FaRegFileAlt } from "react-icons/fa";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import UploadMinimumWagesForm from "../../Component/UploadMinimumWagesForm";
import { IoCloseCircle } from "react-icons/io5";
import api from "../../Services/api";
import NoDataFoundCard from "../../Component/NoDataFoundCard";
import IndustriesDataTable from "../../Component/IndustriesDataTable";
import CustomSnackbar from "../../Component/CustomSnackBar";
import ConfirmDeleteModal from "../../Component/ConfirmDelete";
import { Link } from "react-router-dom";
import { useLoader } from "../../contexts/LoaderContext";
import { FaCircleInfo } from "react-icons/fa6";
import { Autocomplete, TextField, Tooltip, Typography } from "@mui/material";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  height: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto',
};

const MinimumWagesManagement = () => {
  const [minimumWages, setStateWiseMinimumWages] = useState([]);
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' })
  const [deleteWages, setDeleteWages] = useState('');
  const [openIndustryModal, setOpenIndustryModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const { setIsLoading } = useLoader();
  const [industryTypes, setIndustryTypes] = useState([])

  const handleModalOpen = () => {
    setOpen(true)
    setIsEdit(false);
  };

  const handleModalClose = () => {
    setOpen(false);
    if (selectedRow) setSelectedRow(null);
    setIsEdit(false);
  };

  const triggerSnackbar = (type, message) => {
    setSnack({ open: true, message, severity: type });
  };

  const handleClose = () => setSnack({ ...snack, open: false })

  const handleGetFilteredMinimumWages = useCallback(async () => {
    if (selectedState && selectedYear && selectedIndustry) {
      try {
        const response = await api.minimumWages.getMinimumWagesByStateYearIndustry(
          selectedState, selectedYear, selectedIndustry
        );
        if (response.status === 200) {
          const data = response.data.data || [];
          const sorted = [...data].sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
          )
          const latestId = sorted[0]?._id || sorted[0]?.id;
          const marked = data.map(item => ({
            ...item,
            isLatest: (item._id || item.id) === latestId,
          }))
          setStateWiseMinimumWages(marked)
        } else {
          setStateWiseMinimumWages([])
        }
      } catch (error) {
        setStateWiseMinimumWages([])
      } finally {
        setTimeout(() => {
        }, 500);
      }
    } else {
      setStateWiseMinimumWages([]);
    }
  }, [selectedState, selectedYear, selectedIndustry]);

  const handleGetAllStates = async () => {
    try {
      const response = await api.common.getAllStatesApi();
      if (response?.data?.states) {
        const sortedStates = response.data.states.sort((a, b) =>
          a.State.localeCompare(b.State)
        );
        setStates(sortedStates);
        setSelectedState(sortedStates[0].StateId)
      }
    } catch {
      setStates([]);
    }
  }

  useEffect(() => {
    handleGetAllStates();
  }, []);

  useEffect(() => {
    if (selectedState && selectedIndustry && selectedYear) {
      handleGetFilteredMinimumWages()
    } else {
      setStateWiseMinimumWages([])
    }

  }, [selectedState, selectedIndustry, selectedYear, handleGetFilteredMinimumWages])

  const handleUploadMinimumWages = async (values) => {
    const data = {
      ...values,
      fileUrl: uploadedFileUrl
    };

    try {
      const response = await api.minimumWages.createMinimumWages(data);
      if (response.status === 200 || response.status === 201) {
        triggerSnackbar("success", "Minimum wages uploaded successfully")
        handleModalClose();
        handleGetFilteredMinimumWages();
      } else if (response.status === 409) {
        triggerSnackbar('error', response.data.message)
        handleModalClose()
        handleGetFilteredMinimumWages()
      }
    } catch (err) {
      triggerSnackbar("error", err?.response?.data?.message || "Failed to upload minimum wages")
    }
  };

  const handleEditOnWageDetails = async (values) => {
    const data = {
      ...values,
      fileUrl: uploadedFileUrl || values.fileUrl
    }
    if (selectedRow) {
      const id = selectedRow._id || selectedRow.id;
      const response = await api.minimumWages.onEditMinimumWage(id, data);
      if (response.status === 200 || response.status === 201) {
        triggerSnackbar("success", "Minimum wages uploaded successfully")
        handleModalClose()
        handleGetFilteredMinimumWages()
      } else if (response.status === 500) {
        triggerSnackbar("error", response.data.message)
      }
    }
  };

  const handleUploadFile = async (file, stateId, industryType) => {
    setIsLoading(true)
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.common.uploadFileApi(formData, stateId, industryType);
    if (response.status === 200) {
      const fileUrl = response.data.data;
      setUploadedFileUrl(fileUrl);
      triggerSnackbar("success", "File uploaded Successfully");
      setIsLoading(false)
      return true;
    } else {
      triggerSnackbar("error", "Failed to upload File");
      setIsLoading(false)
      return false;
    }
  };

  const handleViewIndustryModal = (row) => {
    setSelectedRow(row);
    setOpenIndustryModal(true)
  };

  const handleCloseIndustryModal = () => {
    setSelectedRow(null)
    setOpenIndustryModal(false);
    setIsEdit(false);
  };

  const handleOnDeleteWages = (id) => {
    setDeleteWages(id)
  };

  const handleOnConfirmDeleteWages = async () => {
    if (deleteWages) {
      const response = await api.minimumWages.onDeleteWagesApi(deleteWages)
      if (response.status === 200) {
        triggerSnackbar("success", response.data.message)
        handleGetFilteredMinimumWages()
        setDeleteWages('')
      } else if (response.status === 500 || response.status === 400) {
        triggerSnackbar("error", response.data.message)
      }
    }
  }

  const handleGetIndustryTypesBasedOnState = useCallback(async () => {
    if (!selectedState) return
    const response = await api.minimumWages.onGetIndustryTypesBasedOnState(selectedState)
    if (response.status === 200) {
      const { data } = response.data
      setIndustryTypes(data)
      setSelectedIndustry(data[0].industryType)
    } else {
      setIndustryTypes([])
      setSelectedIndustry('')
    }
  }, [selectedState]);

  useEffect(() => {
    handleGetIndustryTypesBasedOnState()
  }, [handleGetIndustryTypesBasedOnState])

  const handleOnSetState = (state) => {
    setSelectedState(state)
  }

  return (
    <>
      <CustomSnackbar
        open={snack.open}
        handleClose={handleClose}
        message={snack.message}
        severity={snack.severity}
      />
      <ConfirmDeleteModal
        open={!!deleteWages}
        onConfirm={handleOnConfirmDeleteWages}
        onClose={() => setDeleteWages('')}
      />
      <Modal open={open} onClose={handleModalClose}>
        <Box sx={style}>
          <div className="flex justify-end w-full p-2">
            <IoCloseCircle className="text-xl cursor-pointer" onClick={handleModalClose} />
          </div>
          <UploadMinimumWagesForm
            selectedIndustryType={selectedIndustry}
            onSetState={handleOnSetState}
            selectedState={selectedState}
            states={states}
            industryTypes={industryTypes}
            onUploadFile={handleUploadFile}
            onUploadMinimumWages={handleUploadMinimumWages}
            onEdit={handleEditOnWageDetails}
            data={selectedRow}
            isEdit={isEdit}
          />
        </Box>
      </Modal>
      <Modal open={!!selectedRow && openIndustryModal} onClose={handleCloseIndustryModal}>
        <Box sx={style}>
          <div className="flex justify-end w-full p-2">
            <IoCloseCircle className="text-xl cursor-pointer" onClick={handleCloseIndustryModal} />
          </div>
          <IndustriesDataTable
            data={[selectedRow]}
            state={selectedRow?.State || ''}
            showIndustryType={true}
            showActions={false}
          />
        </Box>
      </Modal>
     
        <div className="d-grid justify-content-center  m-4 mb-5 text-center">
          <h1 className="text-2xl  font-bold text-gray-800">
            Minimum Wage File Management
          </h1>
          <p className="text-gray-500 text-sm sm:text-base">
            Upload and manage minimum wage documents for Indian states
          </p>
        </div>
     
      <div className="flex justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <FaRegFileAlt className="text-xl text-blue-500" />
          <h1 className="text-md">Uploaded Files ({minimumWages.length})</h1>
        </div>        <button
          onClick={handleModalOpen}
          className={`p-2 bg-primary-color text-white flex gap-2 items-center rounded-md font-semibold`}>
          <LuUpload className="text-md" />
          Upload Wages
        </button>
      </div>
      <div className="px-4">
        <div className="bg-white pt-4 px-4 rounded-md shadow-card-shadow-three">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label htmlFor="selectState" className="mb-1 font-medium text-gray-700">
                State
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>Select State</option>
                {states.map((state) => (
                  <option key={state.StateId} value={state.StateId}>
                    {state.State}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-7">
              <Autocomplete
                options={industryTypes}
                getOptionLabel={(option) => option.industryType}
                value={industryTypes.find(i => i.industryType === selectedIndustry) || null}
                onChange={(e, val) => setSelectedIndustry(val?.industryType || '')}
                filterOptions={(options, state) => {
                  const text = state.inputValue.trim().toLowerCase();
                  if (text === '') return options;
                  if (text.length < 3) return [];
                  return options.filter((o) =>
                    o.industryType.toLowerCase().includes(text)
                  );
                }}
                openOnFocus={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Industry"
                    placeholder="Type to search (min 3 letters)"
                    size="small"
                  />
                )}
                renderOption={(props, option, { inputValue }) => {
                  const text = option.industryType;
                  const searchTerm = inputValue.trim();
                  const regex = new RegExp(`(${searchTerm})`, 'gi');
                  const parts = text.split(regex);

                  const highlightedText = parts.map((part, i) =>
                    part.toLowerCase() === searchTerm.toLowerCase() ? (
                      <span key={i} className="bg-yellow-300 font-semibold">
                        {part}
                      </span>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  );

                  const isLong = text.length > 30;

                  return (
                    <li {...props}>
                      {isLong ? (
                        <Tooltip
                          title={
                            <Box
                              sx={{
                                maxWidth: 300,
                                maxHeight: 150,
                                overflowY: 'auto',
                                whiteSpace: 'normal',
                                p: 1,
                                bgcolor: 'white',
                                color: 'black',
                                borderRadius: 1,
                                boxShadow: 3,
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                                {highlightedText}
                              </Typography>
                            </Box>
                          }
                          placement="right"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'transparent',
                                p: 0,
                              },
                            },
                          }}
                        >
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              maxWidth: '250px',
                            }}
                          >
                            {highlightedText}
                          </span>
                        </Tooltip>
                      ) : (
                        <span
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            maxWidth: '250px',
                          }}
                        >
                          {highlightedText}
                        </span>
                      )}
                    </li>
                  );
                }}
                fullWidth
                size="small"
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="selectState" className="mb-1 font-medium text-gray-700">
                Year
              </label>
              <select
                name="year"
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                className=" border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>Select Year</option>
                <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                <option value={new Date().getFullYear() + 1}>{new Date().getFullYear() + 1}</option>
              </select>
            </div>
          </div>
          <div>
            {
              minimumWages.length > 0 ?
                <IndustriesDataTable
                  data={minimumWages}
                  onDelete={handleOnDeleteWages}
                  onView={handleViewIndustryModal}
                  showActions={true}
                  onEdit={(row) => {
                    setSelectedRow(row);
                    handleModalOpen();
                    setIsEdit(true);
                  }}
                />
                :
                industryTypes.length > 0 ?
                  <NoDataFoundCard
                    subText="No data found for selected State, Industry, and Year."
                    className="min-h-full shadow-none"
                    onClick={handleModalOpen}
                  /> :
                  <div className="w-full p-4 flex flex-col justify-center gap-4 items-center mt-4">
                    <FaCircleInfo className="text-3xl text-gray-600" />
                    <p className="text-center text-gray-700">You haven’t added any Industry Types yet.</p>
                    <Link to="/industry-type-management" className="w-fit">
                      <div className="p-2 bg-primary-color text-white flex gap-2 items-center rounded-md w-fit hover:opacity-90 transition">
                        <FaPlus />
                        Create Industry Type
                      </div>
                    </Link>
                  </div>
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default MinimumWagesManagement;
